import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import WeatherSlide from '../components/WeatherSlide';
import AlertBar from '../components/AlertBar';
import ZoneLayout from '../components/ZoneLayout';
import useFullyKiosk from '../hooks/useFullyKiosk';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default FrostByte IT ad - stored locally
const DEFAULT_AD = '/frostbyte_signage_ad.png';

// Helper function to convert YouTube URLs to embed format
const getYouTubeEmbedUrl = (url) => {
  if (!url) return url;
  if (url.includes('/embed/')) return url;
  let videoId = null;
  const watchMatch = url.match(/[?&]v=([^&]+)/);
  if (watchMatch) videoId = watchMatch[1];
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) videoId = shortMatch[1];
  const vMatch = url.match(/youtube\.com\/v\/([^?&]+)/);
  if (vMatch) videoId = vMatch[1];
  if (videoId) return `https://www.youtube.com/embed/${videoId}`;
  return url;
};

const isYouTubeUrl = (url) => {
  return url && (url.includes('youtube.com') || url.includes('youtu.be'));
};

const DynamicPage = () => {
  const { pageId } = useParams();
  const [content, setContent] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [orientation, setOrientation] = useState('landscape');
  const [weatherZipCode, setWeatherZipCode] = useState(null);
  const [weatherEnabled, setWeatherEnabled] = useState(true);
  const [slideDuration, setSlideDuration] = useState(5);
  const [showDefaultAd, setShowDefaultAd] = useState(true);
  const [alertText, setAlertText] = useState(null);
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [zoneLayout, setZoneLayout] = useState(false);
  const [zoneClock, setZoneClock] = useState(true);
  const [zoneTicker, setZoneTicker] = useState('');
  const [liveStreamActive, setLiveStreamActive] = useState(false);
  const [liveStreamUrl, setLiveStreamUrl] = useState(null);
  const [liveSchedule, setLiveSchedule] = useState(null);
  const [displayId] = useState(() => {
    let id = localStorage.getItem(`display_id_${pageId}`);
    if (!id) {
      id = `display_${pageId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(`display_id_${pageId}`, id);
    }
    return id;
  });
  useFullyKiosk(displayId);
  const videoRef = useRef(null);
  const lastRefreshRef = useRef(Date.now());

  // Check if current time is within the scheduled live stream window
  const isScheduledLiveTime = useCallback(() => {
    if (!liveSchedule || !liveSchedule.enabled) return false;
    
    const now = new Date();
    // Convert to the scheduled timezone
    const tzOptions = { timeZone: liveSchedule.timezone || 'America/Chicago' };
    const localTime = new Date(now.toLocaleString('en-US', tzOptions));
    
    // Check day of week (0=Sunday, 1=Monday, etc.)
    const dayMap = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
    const scheduledDay = dayMap[liveSchedule.day?.toLowerCase()] ?? -1;
    if (localTime.getDay() !== scheduledDay) return false;
    
    // Check time window
    const [startH, startM] = (liveSchedule.start_time || '08:50').split(':').map(Number);
    const [endH, endM] = (liveSchedule.end_time || '11:00').split(':').map(Number);
    const currentMinutes = localTime.getHours() * 60 + localTime.getMinutes();
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    
    return currentMinutes >= startMinutes && currentMinutes < endMinutes;
  }, [liveSchedule]);

  // Poll every 30 seconds to check schedule
  const [showLiveStream, setShowLiveStream] = useState(false);
  
  useEffect(() => {
    const checkLive = () => {
      // Manual override takes priority
      if (liveStreamActive && liveStreamUrl) {
        setShowLiveStream(true);
        return;
      }
      // Then check schedule
      if (isScheduledLiveTime() && liveSchedule?.embed_url) {
        setShowLiveStream(true);
        return;
      }
      setShowLiveStream(false);
    };
    
    checkLive();
    const interval = setInterval(checkLive, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, [liveStreamActive, liveStreamUrl, liveSchedule, isScheduledLiveTime]);

  // Send health check with detailed info
  const sendHealthCheck = useCallback(async () => {
    try {
      const healthData = {
        display_id: displayId,
        page_id: pageId,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        orientation: window.screen.orientation?.type || (window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'),
        user_agent: navigator.userAgent,
        connection_type: navigator.connection?.effectiveType || 'Unknown',
        current_item_index: currentIndex,
        total_items: content.length
      };
      await axios.post(`${API}/health/check-in`, healthData);
    } catch (error) {
      console.error('Health check failed:', error);
    }
  }, [displayId, pageId, currentIndex, content.length]);

  // Load content from database
  const loadContent = useCallback(async () => {
    try {
      // First check page status
      const statusRes = await axios.get(`${API}/admin/page-status/${pageId}`);
      setEnabled(statusRes.data.enabled);
      setOrientation(statusRes.data.orientation || 'landscape');
      setWeatherZipCode(statusRes.data.weather_zip_code || null);
      const isWeatherEnabled = statusRes.data.weather_enabled !== false;
      setWeatherEnabled(isWeatherEnabled);
      setSlideDuration(statusRes.data.slide_duration || 5);
      const adEnabled = statusRes.data.show_default_ad !== false;
      setShowDefaultAd(adEnabled);
      setAlertText(statusRes.data.alert_text || null);
      setAlertEnabled(statusRes.data.alert_enabled || false);
      setZoneLayout(statusRes.data.zone_layout || false);
      setZoneClock(statusRes.data.zone_clock !== false);
      setZoneTicker(statusRes.data.zone_ticker || '');
      
      // Live stream handling
      setLiveStreamUrl(statusRes.data.live_stream_url || null);
      setLiveStreamActive(statusRes.data.live_stream_active || false);
      setLiveSchedule(statusRes.data.live_schedule || null);

      if (!statusRes.data.enabled) {
        setLoading(false);
        return;
      }

      // Try to get playlist content first
      try {
        const playlistRes = await axios.get(`${API}/pages/${pageId}/current-playlist`);
        if (playlistRes.data.items && playlistRes.data.items.length > 0) {
          const allContent = [
            ...(isWeatherEnabled ? [{ type: 'weather', id: 'weather-slide' }] : []),
            ...playlistRes.data.items,
            ...(adEnabled ? [{ type: 'image', url: DEFAULT_AD }] : [])
          ];
          setContent(allContent);
          setLoading(false);
          return;
        }
      } catch (err) {
        // No playlist active, continue with regular content
      }

      // Get page content
      const response = await axios.get(`${API}/admin/pages/${pageId}`);
      const items = response.data.items || [];
      
      const allContent = [
        ...(isWeatherEnabled ? [{ type: 'weather', id: 'weather-slide' }] : []),
        ...items,
        ...(adEnabled ? [{ type: 'image', url: DEFAULT_AD }] : [])
      ];
      setContent(allContent);
    } catch (error) {
      console.error('Failed to load content:', error);
      setContent([
        { type: 'image', url: DEFAULT_AD }
      ]);
    } finally {
      setLoading(false);
    }
  }, [pageId]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  // Send health check periodically
  useEffect(() => {
    if (enabled && content.length > 0) {
      sendHealthCheck();
      const interval = setInterval(sendHealthCheck, 60000);
      return () => clearInterval(interval);
    }
  }, [enabled, content.length, sendHealthCheck]);

  // Smart refresh - wait for video to finish before reloading
  useEffect(() => {
    const REFRESH_INTERVAL = 60 * 60 * 1000; // 1 hour

    const checkRefresh = () => {
      const now = Date.now();
      const timeSinceRefresh = now - lastRefreshRef.current;

      if (timeSinceRefresh >= REFRESH_INTERVAL) {
        const currentItem = content[currentIndex];
        
        // If currently playing a video, wait for it to finish
        if (currentItem?.type === 'video' && videoRef.current) {
          const video = videoRef.current;
          if (!video.paused && !video.ended) {
            // Wait for video to end
            const handleEnded = () => {
              video.removeEventListener('ended', handleEnded);
              window.location.reload();
            };
            video.addEventListener('ended', handleEnded);
            return;
          }
        }
        
        // Refresh immediately
        window.location.reload();
      }
    };

    const interval = setInterval(checkRefresh, 30000);
    return () => clearInterval(interval);
  }, [content, currentIndex]);

  // Auto-advance slides
  useEffect(() => {
    if (content.length === 0 || !enabled) return;

    const currentItem = content[currentIndex];

    // For videos, wait for them to end
    if (currentItem.type === 'video' && !currentItem.url?.includes('youtube.com')) {
      // Non-YouTube videos - handled by onEnded event
      return;
    }

    // Weather slide shows for configured duration
    if (currentItem.type === 'weather') {
      const dur = (currentItem.duration || slideDuration) * 1000;
      const timer = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % content.length);
      }, dur);
      return () => clearTimeout(timer);
    }

    // Images show for per-slide or global duration, YouTube videos for 30s
    const dur = currentItem.duration
      ? currentItem.duration * 1000
      : (currentItem.type === 'video' ? 30000 : slideDuration * 1000);
    const timer = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % content.length);
    }, dur);

    return () => clearTimeout(timer);
  }, [currentIndex, content, enabled]);

  const handleVideoEnd = () => {
    setCurrentIndex((prev) => (prev + 1) % content.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-cyan-400 text-2xl animate-pulse">Loading...</div>
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Display Offline</h1>
          <p className="text-slate-400">This display has been disabled by the administrator.</p>
        </div>
      </div>
    );
  }

  if (content.length === 0 && !showLiveStream) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">No Content</h1>
          <p className="text-slate-400">No content has been added to this display yet.</p>
        </div>
      </div>
    );
  }

  // Live stream takes over the entire display
  if (showLiveStream) {
    const streamUrl = liveStreamActive && liveStreamUrl
      ? (isYouTubeUrl(liveStreamUrl) ? `${getYouTubeEmbedUrl(liveStreamUrl)}?autoplay=1&mute=0` : liveStreamUrl)
      : liveSchedule?.embed_url;
    
    return (
      <div className="min-h-screen bg-black overflow-hidden" data-testid="live-stream-display">
        <iframe
          src={streamUrl}
          className="w-full h-screen"
          allow="autoplay; encrypted-media; fullscreen"
          allowFullScreen
          title="Live Stream"
          style={{ border: 'none' }}
        />
        <AlertBar text={alertText} enabled={alertEnabled} />
      </div>
    );
  }

  const currentItem = content[currentIndex];
  const isPortrait = orientation === 'portrait';

  // Portrait mode styles
  const portraitContainerStyles = isPortrait ? {
    transform: 'rotate(90deg)',
    transformOrigin: 'center center',
    width: '100vh',
    height: '100vw',
    position: 'fixed',
    top: '50%',
    left: '50%',
    marginTop: '-50vw',
    marginLeft: '-50vh'
  } : {};

  const slideContent = (
    <>
      {currentItem.type === 'weather' && (
        <WeatherSlide isPortrait={isPortrait} zipCode={weatherZipCode} />
      )}
      {currentItem.type === 'image' && (
        <img src={currentItem.url} alt="Display content" className="w-full h-full object-contain" />
      )}
      {currentItem.type === 'video' && (
        isYouTubeUrl(currentItem.url) ? (
          <iframe
            src={`${getYouTubeEmbedUrl(currentItem.url)}?autoplay=1&mute=1&controls=0&loop=1&playlist=${getYouTubeEmbedUrl(currentItem.url).split('/embed/')[1]}`}
            className="w-full h-full"
            allow="autoplay; encrypted-media"
            allowFullScreen
            title="Video content"
          />
        ) : (
          <video ref={videoRef} src={currentItem.url} className="w-full h-full object-contain" autoPlay muted onEnded={handleVideoEnd} />
        )
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-black flex items-center justify-center overflow-hidden">
      <div className="w-full h-screen" style={portraitContainerStyles}>
        {zoneLayout ? (
          <ZoneLayout ticker={zoneTicker} showClock={zoneClock}>
            <div className="w-full h-full flex items-center justify-center">
              {slideContent}
            </div>
          </ZoneLayout>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {slideContent}
          </div>
        )}
      </div>
      <AlertBar text={alertText} enabled={alertEnabled} />
    </div>
  );
};

export default DynamicPage;
